<template>
  <Transition name="loading" appear>
    <div 
      v-if="props.visible"
      class="fixed inset-0 z-50 flex items-center justify-center bg-background backdrop-blur-sm"
    >
      <!-- 主要内容容器 -->
      <div class="flex flex-col items-center space-y-8">
        <!-- Logo 区域 -->
        <div class="relative">
          <!-- Logo 容器 -->
          <div class="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
            <!-- 创诚AI Logo - 橙色背景创字 -->
            <div class="relative w-full h-full bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl shadow-lg animate-pulse">
              <!-- 创字的SVG形状 -->
              <svg 
                class="w-full h-full" 
                viewBox="0 0 100 100" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <!-- 创字 - 使用中文字体 -->
                <text 
                  x="50" 
                  y="65" 
                  text-anchor="middle" 
                  fill="white" 
                  font-family="'Microsoft YaHei', 'PingFang SC', 'Hiragino Sans GB', sans-serif" 
                  font-size="50" 
                  font-weight="bold"
                >
                  创
                </text>
              </svg>
            </div>
          </div>
          
          <!-- 旋转动画环 -->
          <div class="absolute inset-0 border-4 border-orange-400/20 border-t-orange-500 rounded-full animate-spin"></div>
        </div>
        
        <!-- 公司信息 -->
        <div class="text-center space-y-4">
          <!-- 公司全名 -->
          <h1 class="text-2xl md:text-3xl font-bold text-foreground animate-fade-in">
            深圳市创诚人工智能科技有限公司
          </h1>
          
          <!-- 公司英文名/标语 -->
          <p class="text-lg md:text-xl text-muted-foreground animate-fade-in-delay">
            Shenzhen Chengcheng AI Technology Co., Ltd.
          </p>
          
          <!-- 加载文本 -->
          <div class="flex items-center justify-center space-x-2 text-orange-500">
            <div class="flex space-x-1">
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
            <span class="text-base font-medium">{{ loadingText }}</span>
          </div>
        </div>
        
        <!-- 进度条 -->
        <div class="w-64 md:w-80 bg-muted rounded-full h-2 overflow-hidden">
          <div 
            class="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-300 ease-out"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
        
        <!-- 百分比显示 -->
        <div class="text-sm text-muted-foreground font-mono">
          {{ Math.round(progress) }}%
        </div>
      </div>
      
      <!-- 背景装饰元素 -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <!-- 装饰圆圈 -->
        <div class="absolute top-1/4 left-1/4 w-32 h-32 border border-orange-400/10 rounded-full animate-spin-slow"></div>
        <div class="absolute bottom-1/4 right-1/4 w-48 h-48 border border-orange-400/5 rounded-full animate-spin-reverse"></div>
        
        <!-- 浮动粒子效果 -->
        <div class="absolute top-1/3 left-1/2 w-2 h-2 bg-orange-400/20 rounded-full animate-float"></div>
        <div class="absolute bottom-1/3 left-1/3 w-1 h-1 bg-orange-500/30 rounded-full animate-float-delay"></div>
        <div class="absolute top-1/2 right-1/3 w-3 h-3 bg-orange-400/10 rounded-full animate-float-delay-2"></div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'

interface Props {
  visible?: boolean
  duration?: number
  minDuration?: number
}

const props = withDefaults(defineProps<Props>(), {
  visible: true,
  duration: 3000,
  minDuration: 1500
})

const emit = defineEmits<{
  complete: []
}>()

const progress = ref(0)
const loadingText = ref('正在加载...')

const loadingTexts = [
  '正在加载...',
  '加载中...',
  '准备就绪...',
  '欢迎使用创诚AI...'
]

let progressInterval: NodeJS.Timeout | null = null
let textInterval: NodeJS.Timeout | null = null

const startLoading = () => {
  progress.value = 0
  
  // 进度条动画
  progressInterval = setInterval(() => {
    if (progress.value < 100) {
      // 非线性进度增长，开始快，后面慢
      const increment = Math.max(1, (100 - progress.value) * 0.05)
      progress.value = Math.min(100, progress.value + increment)
    }
    
    if (progress.value >= 100) {
      setTimeout(() => {
        emit('complete')
      }, 500) // 完成后延迟一点再触发
    }
  }, 50)
  
  // 文本切换动画
  let textIndex = 0
  textInterval = setInterval(() => {
    textIndex = (textIndex + 1) % loadingTexts.length
    loadingText.value = loadingTexts[textIndex]
  }, 800)
  
  // 确保最少显示时间
  setTimeout(() => {
    if (progress.value < 90) {
      progress.value = 90
    }
  }, props.minDuration)
  
  // 最大持续时间
  setTimeout(() => {
    progress.value = 100
  }, props.duration)
}

const cleanup = () => {
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
  if (textInterval) {
    clearInterval(textInterval)
    textInterval = null
  }
}

onMounted(() => {
  if (props.visible) {
    startLoading()
  }
})

onUnmounted(() => {
  cleanup()
})

// 监听 visible 变化
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    startLoading()
  } else {
    cleanup()
  }
})
</script>

<style scoped>
/* 动画定义 */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-delay {
  0%, 30% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes spin-reverse {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

@keyframes float-delay {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(-180deg);
  }
}

@keyframes float-delay-2 {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-25px) rotate(90deg);
  }
}

/* 应用动画 */
.animate-fade-in {
  animation: fade-in 1s ease-out;
}

.animate-fade-in-delay {
  animation: fade-in-delay 1.5s ease-out;
}

.animate-spin-slow {
  animation: spin-slow 8s linear infinite;
}

.animate-spin-reverse {
  animation: spin-reverse 12s linear infinite;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delay {
  animation: float-delay 4s ease-in-out infinite 1s;
}

.animate-float-delay-2 {
  animation: float-delay-2 5s ease-in-out infinite 2s;
}

/* Transition 效果 */
.loading-enter-active,
.loading-leave-active {
  transition: all 0.5s ease;
}

.loading-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.loading-leave-to {
  opacity: 0;
  transform: scale(1.05);
}
</style>